import { Unsubscribe } from "../lib/redux";
import { NetworkStates } from "../store/mobile/types";
import { DriveStore } from "../store/reducers";
import { ICredentials } from "../utils/credentialsStore";
import { getServerDate } from "../utils/storage/date";
import { observeStore } from "../utils/store";
import Service from "./base";
import * as logger from "../utils/logger";
import constants from "../constants";

/**
 * IamSessionIdManager is a service for IAM user where the user add-in requires a sessionId
 * It will fetch a sessionId using the JWT access token
 * The sessionId will be EXTENDED every x minutes, determine by the refreshTimer (currently 25 minutes).
 *
 * Additionally, we have a listener that check if we have a network state change,
 * if the new state is online, then we will manually request an sessionId extension
 * this is used to handle the case where the driveApp goes offline and comes back online with an expired sessionId
 *
 * On service destroy call, it will invalidate the sessionId and stop the refresh timer.
 */
export class IamSessionIdManager extends Service{
    private lastManualSessionExtension: Date | undefined = undefined;
    private timerNumber: number | undefined = undefined;
    private sessionToken: ICredentials | undefined = undefined;
    private promise: Promise<string> | undefined = undefined;
    private lastSessionValidation: Date | undefined = undefined;
    private readonly initDate: number | undefined = undefined;
    private readonly checkSessionId: ((credential: any) => Promise<any>) | undefined;

    constructor(
        private readonly refreshTimer: number,
        private readonly requestSessionId: () => Promise<string>,
        private readonly requestToExtendSessionId: (sessionId: string) => Promise<void>,
        private readonly logout: (session: ICredentials) => Promise<void>,
        private readonly errorHandler: (error) => void,
        private readonly store: DriveStore,
        checkSessionId?: (credential: any) => Promise<any>
    ) {
        super();
        this.checkSessionId = checkSessionId;
        this.initDate = getServerDate().getTime();
    }

    async getSessionId (credentials: ICredentials) {
        try {
            // if token already exists, validate and return it
            if (this.sessionToken && this.sessionToken.sessionId) {
                const now = getServerDate().getTime();
                const shouldValidate = !this.lastSessionValidation || this.lastSessionValidation.getTime() + constants.IAM.IAM_ADDIN_MANAGER_SESSION_VALIDATION_DEBOUNCE < now;
                if (shouldValidate) {
                    this.lastSessionValidation = getServerDate();
                    this.promise = this.validateSession(this.sessionToken.sessionId, credentials);
                    return await this.promise;
                }
                return this.sessionToken.sessionId;
            }

            // if there is an ongoing request, wait for it to finish
            // and return the sessionId from the resulting promise
            if (this.promise) {
                return await this.promise;
            }

            this.startSubscriptions();
            return await this.nextTick(credentials);
        } finally {
            this.promise = undefined;
        }
    }

    async destroy() {
        if (this.sessionToken && this.sessionToken.sessionId) {
            await this.logout(this.sessionToken);
        }

        this.sessionToken = undefined;
        this.storeUnsubscribe && this.storeUnsubscribe();
        this.resetTimer();
    }

    private storeUnsubscribe: Unsubscribe = () => { };

    private async manualSessionExtension() {
        if (this.store.getState().mobile.online === NetworkStates.ONLINE
            && this.sessionToken?.sessionId
            && (!this.lastManualSessionExtension ||
                (this.lastManualSessionExtension && this.lastManualSessionExtension.getTime() + this.refreshTimer < getServerDate().getTime()))) {
            logger.log(
                logger.Source.IAM,
                `IamAddinManager.manualSessionExtension: manually extending session due to network state change ${ this.sessionToken.sessionId.substring(this.sessionToken.sessionId.length - 3) } ${ this.initDate }`,
                this.sessionToken?.userName
            );
            this.lastManualSessionExtension = getServerDate();
            await this.requestToExtendSessionId(this.sessionToken.sessionId);
        }
    }

    private startSubscriptions(){
        this.stopSubscriptions();
        this.storeUnsubscribe = observeStore(this.store, state => state.mobile.online, this.manualSessionExtension.bind(this));
    }

    private stopSubscriptions() {
        this.storeUnsubscribe();
    }

    private resetTimer() {
        if (this.timerNumber) {
            window.clearTimeout(this.timerNumber);
        }

        this.timerNumber = undefined;
    }

    private async newSession(credentials: ICredentials) {
        logger.log(
            logger.Source.IAM,
            `IamAddinManager.newSession: requesting new session ${ this.initDate }`,
            this.sessionToken?.userName
        );

        try {
            let sessionId = await this.requestSessionId();
            this.sessionToken = {
                userName: credentials.userName,
                database: credentials.database,
                sessionId: sessionId
            };

            logger.log(
                logger.Source.IAM,
                `IamAddinManager.newSession: new session ${ sessionId.substring(sessionId.length - 3) } ${ this.initDate }`,
                this.sessionToken?.userName
            );
            return sessionId;
        } catch (error) {
            logger.error(logger.Source.IAM, `IamAddinManager.newSession: failed to fetch new sessionId. ${ error.message }`, credentials.userName);
            return undefined;
        }
    }

    private async renewSessionId(credentials: ICredentials) {
        if (this.sessionToken?.sessionId) {
            logger.log(
                logger.Source.IAM,
                `IamAddinManager.renewSession: extending sessionId ${ this.sessionToken.sessionId.substring(this.sessionToken.sessionId.length - 3) } ${ this.initDate }`,
                this.sessionToken?.userName
            );
            await this.requestToExtendSessionId(this.sessionToken.sessionId);
            return;
        }

        await this.newSession(credentials);
    }

    private nextTick(credentials: ICredentials): Promise<string> {
        this.promise = this.renewSessionId(credentials)
            .catch(error => {
                logger.error(logger.Source.IAM, `IamAddinManager: error: ${ error } ${ this.initDate }`);
                this.errorHandler(error);
                return true;
            })
            .then((_) => {
                this.resetTimer();
                this.timerNumber = window.setTimeout(
                    () => {
                        this.nextTick(credentials);
                    },
                    this.refreshTimer
                );
            })
            .then(() => this.sessionToken?.sessionId || "");

        return this.promise;
    }

    private async validateSession(sessionId: string, credentials: ICredentials): Promise<string> {
        if (!this.checkSessionId) {
            return sessionId;
        }

        try {
            await this.checkSessionId({
                database: credentials?.database,
                sessionId: sessionId,
                userName: credentials?.userName
            });
            return sessionId;
        } catch (error) {
            logger.error(logger.Source.IAM, `IamAddinManager.validateSession: checkSession failed, error: ${ error.message }`, credentials.userName);
            if (!(error.data?.type === "InvalidUserException" || error.data?.type === "InvalidMyAdminUserException" || error.data?.type === "InvalidCredentialsError")) {
                return sessionId;
            }

            let newSessionId = await this.newSession(credentials);
            if (newSessionId !== undefined) {
                return newSessionId;
            }

            return sessionId;
        }
    }
}